// src/components/modals/EditGRNModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Package, Save, Loader2, AlertCircle, CheckCircle, XCircle } from 'lucide-react'
import GRNService from '../../services/grnService'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message}</span>
    <button onClick={onClose} className="ml-2 hover:bg-black hover:bg-opacity-20 rounded p-1">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const EditGRNModal = ({ grn, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState(null)
  
  // Form state
  const [formData, setFormData] = useState({
    receivedBy: '',
    receivedDate: '',
    receivedItems: [],
    note: ''
  })

  // Validation errors
  const [errors, setErrors] = useState({})

  // Check if payment is pending (only allow edit if unpaid or partially paid)
  const isPaymentPending = () => {
    if (!grn) return false
    
    const remainingPayment = parseFloat(grn.remainingPayment)
    
    // If we have valid remaining payment info
    if (!isNaN(remainingPayment)) {
      return remainingPayment > 0
    }
    
    // Fallback to payment status
    const paymentStatus = grn.paymentStatus?.toLowerCase()
    return paymentStatus === 'unpaid' || paymentStatus === 'partially_paid' || paymentStatus === 'partial'
  }

  // Initialize form data
  useEffect(() => {
    const initializeForm = async () => {
      if (grn) {
        console.log('🔍 GRN data for form initialization:', grn)
        console.log('🔍 Received items structure:', grn.receivedItems)
        
        try {
          // Fetch the full purchase order to get orderItems with correct poItemId
          const poId = grn.purchaseOrder?._id || grn.purchaseOrder
          console.log('🔍 Fetching PO data for ID:', poId)
          
          let purchaseOrderItems = []
          
          if (poId) {
            try {
              const poResponse = await GRNService.getAvailablePurchaseOrders()
              const fullPO = poResponse?.data?.purchaseOrders?.find(po => po._id === poId) ||
                            poResponse?.data?.data?.find(po => po._id === poId)
              
              if (fullPO?.orderItems) {
                purchaseOrderItems = fullPO.orderItems
                console.log('✅ Fetched PO orderItems:', purchaseOrderItems)
              } else {
                console.warn('⚠️ No orderItems in fetched PO data')
              }
            } catch (poError) {
              console.error('❌ Error fetching PO data:', poError)
            }
          }
          
          setFormData({
            receivedBy: grn.receivedBy || '',
            receivedDate: grn.receivedDate ? new Date(grn.receivedDate).toISOString().slice(0, 16) : '',
            receivedItems: grn.receivedItems?.map((item, index) => {
              console.log(`🔍 Item ${index} full structure:`, JSON.stringify(item, null, 2))
              
              // Try to find matching PO item by productId or rawMaterialId
              let poItemId = null
              if (purchaseOrderItems.length > 0) {
                const matchingPOItem = purchaseOrderItems.find(poItem => 
                  (item.productId && poItem.productId === item.productId) ||
                  (item.rawMaterialId && poItem.rawMaterialId === item.rawMaterialId)
                )
                poItemId = matchingPOItem?._id
                console.log(`🔍 Matched poItemId for item ${index}:`, poItemId)
              }
              
              if (!poItemId) {
                console.error(`❌ Could not find poItemId for item ${index}!`)
              }
              
              // Format expiry date for datetime-local input
              const formattedExpiryDate = item.expiryDate ? new Date(item.expiryDate).toISOString().slice(0, 16) : ''
              console.log(`🔍 Item ${index} expiryDate:`, item.expiryDate, '-> formatted:', formattedExpiryDate)
              
              return {
                ...item, // Spread first to get all item properties
                poItemId: poItemId, // Set the matched poItemId
                receivedQuantity: item.receivedQuantity || 0,
                unitPrice: item.unitPrice || 0,
                expiryDate: formattedExpiryDate // Override with formatted date last
              }
            }) || [],
            note: grn.note || ''
          })
        } catch (error) {
          console.error('❌ Error initializing form:', error)
          // Still set form data with whatever we have
          setFormData({
            receivedBy: grn.receivedBy || '',
            receivedDate: grn.receivedDate ? new Date(grn.receivedDate).toISOString().slice(0, 16) : '',
            receivedItems: grn.receivedItems?.map(item => ({
              ...item,
              receivedQuantity: item.receivedQuantity || 0,
              unitPrice: item.unitPrice || 0,
              expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString().slice(0, 16) : ''
            })) || [],
            note: grn.note || ''
          })
        }
      }
    }
    
    initializeForm()
  }, [grn])

  // Show toast
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.receivedBy.trim()) {
      newErrors.receivedBy = 'Received by is required'
    }

    if (!formData.receivedDate) {
      newErrors.receivedDate = 'Received date is required'
    }

    // Validate received items
    if (formData.receivedItems.length === 0) {
      newErrors.receivedItems = 'At least one received item is required'
    } else {
      formData.receivedItems.forEach((item, index) => {
        if (!item.receivedQuantity || item.receivedQuantity <= 0) {
          newErrors[`receivedQuantity_${index}`] = 'Received quantity must be greater than 0'
        }
        
        // Check if received quantity exceeds ordered quantity
        const orderedQuantity = item.orderedQuantity || 0
        if (item.receivedQuantity > orderedQuantity) {
          newErrors[`receivedQuantity_${index}`] = `Cannot receive more than ordered quantity (${orderedQuantity})`
        }
        
        if (!item.unitPrice || item.unitPrice <= 0) {
          newErrors[`unitPrice_${index}`] = 'Unit price must be greater than 0'
        }
        
        // Validate expiry date - required if received quantity > 0
        if (item.receivedQuantity > 0 && (!item.expiryDate || item.expiryDate.trim() === '')) {
          newErrors[`expiryDate_${index}`] = 'Expiry date is required for received items'
        }
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  // Handle received item changes
  const handleItemChange = (index, field, value) => {
    // Check for quantity validation in real-time
    if (field === 'receivedQuantity') {
      const currentItem = formData.receivedItems[index]
      const orderedQuantity = currentItem.orderedQuantity || 0
      const newQuantity = parseFloat(value) || 0
      
      if (newQuantity > orderedQuantity) {
        showToast(`Cannot receive more than ordered quantity (${orderedQuantity})`, 'warning')
        // Don't update the value if it exceeds ordered quantity
        return
      }
    }
    
    setFormData(prev => ({
      ...prev,
      receivedItems: prev.receivedItems.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }))
    
    // Clear error when user starts typing
    const errorKey = `${field}_${index}`
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: undefined
      }))
    }
  }

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      showToast('Please fix the validation errors', 'error')
      return
    }

    try {
      setLoading(true)

      // Prepare data for API - send only allowed fields based on backend validation
      const updateData = {
        receivedBy: formData.receivedBy,
        receivedDate: formData.receivedDate,
        receivedItems: formData.receivedItems.map((item, index) => {
          console.log(`🔍 Preparing item ${index} for API:`, item)
          console.log(`🔍 Item poItemId from form:`, item.poItemId)
          
          // Use the poItemId that was preserved from the original GRN data
          // This should already be the correct PO item ID from when the GRN was created
          const poItemId = item.poItemId
          
          if (!poItemId) {
            console.error(`❌ No poItemId found for item ${index}!`, item)
          } else {
            console.log(`✅ Using poItemId: ${poItemId}`)
          }
          
          // Send ONLY the fields that backend validation allows
          const itemData = {
            poItemId: poItemId, // Use the preserved poItemId from original GRN
            receivedQuantity: parseFloat(item.receivedQuantity),
            unitPrice: parseFloat(item.unitPrice)
          }
          
          // Only include expiryDate if it has a value
          if (item.expiryDate && item.expiryDate.trim() !== '') {
            itemData.expiryDate = item.expiryDate
          }
          
          return itemData
        }),
        note: formData.note
      }

      console.log('📦 Updating GRN with only allowed fields:', updateData)
      console.log('🔧 Strategy: Send only poItemId, receivedQuantity, unitPrice per backend validation')

      const response = await GRNService.updateGRN(grn._id, updateData)

      if (response?.success) {
        showToast('GRN updated successfully', 'success')
        setTimeout(() => {
          onSuccess?.()
          onClose()
        }, 1500)
      } else {
        throw new Error(response?.message || 'Failed to update GRN')
      }
    } catch (error) {
      console.error('❌ Error updating GRN:', error)
      showToast(error.message || 'Failed to update GRN', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Don't render if payment is not pending
  if (!isPaymentPending()) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Edit GRN</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="text-center py-8">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Cannot Edit GRN</h3>
            <p className="text-gray-600 mb-4">
              This GRN can only be edited when payment is pending. 
              Current status: {grn?.paymentStatus || 'Unknown'}
            </p>
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Toast */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-4xl mx-4 flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Package className="h-6 w-6 text-blue-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Edit GRN</h2>
                <p className="text-sm text-gray-600">
                  {grn?.grnNumber} - {grn?.supplier?.name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Received By *
                  </label>
                  <Input
                    value={formData.receivedBy}
                    onChange={(e) => handleInputChange('receivedBy', e.target.value)}
                    placeholder="Enter receiver name"
                    className={errors.receivedBy ? 'border-red-500' : ''}
                  />
                  {errors.receivedBy && (
                    <p className="text-red-500 text-sm mt-1">{errors.receivedBy}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Received Date *
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.receivedDate}
                    onChange={(e) => handleInputChange('receivedDate', e.target.value)}
                    className={errors.receivedDate ? 'border-red-500' : ''}
                  />
                  {errors.receivedDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.receivedDate}</p>
                  )}
                </div>
              </div>

              {/* Received Items */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Received Items</h3>
                <div className="space-y-4">
                  {formData.receivedItems.map((item, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {item.productSnapshot?.name || item.rawMaterialSnapshot?.name || 'Unknown Item'}
                          </h4>
                          <p className="text-sm text-gray-600">
                            Type: {item.itemType || 'Unknown'} | 
                            Ordered: {item.orderedQuantity || 0}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Received Quantity *
                          </label>
                          <Input
                            type="number"
                            step="any"
                            value={item.receivedQuantity}
                            onChange={(e) => handleItemChange(index, 'receivedQuantity', e.target.value)}
                            placeholder="0.00"
                            className={errors[`receivedQuantity_${index}`] ? 'border-red-500' : ''}
                          />
                          {errors[`receivedQuantity_${index}`] && (
                            <p className="text-red-500 text-sm mt-1">{errors[`receivedQuantity_${index}`]}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Unit Price *
                          </label>
                          <Input
                            type="number"
                            step="any"
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                            placeholder="0.00"
                            className={errors[`unitPrice_${index}`] ? 'border-red-500' : ''}
                          />
                          {errors[`unitPrice_${index}`] && (
                            <p className="text-red-500 text-sm mt-1">{errors[`unitPrice_${index}`]}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Expiry Date & Time *
                        </label>
                        <Input
                          type="datetime-local"
                          value={item.expiryDate || ''}
                          onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                          min={new Date().toISOString().slice(0, 16)}
                          className={errors[`expiryDate_${index}`] ? 'border-red-500' : ''}
                        />
                        {errors[`expiryDate_${index}`] && (
                          <p className="text-red-500 text-sm mt-1">{errors[`expiryDate_${index}`]}</p>
                        )}
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-sm text-gray-600">
                          Total: LKR {((item.receivedQuantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.receivedItems && (
                  <p className="text-red-500 text-sm mt-2">{errors.receivedItems}</p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => handleInputChange('note', e.target.value)}
                  placeholder="Additional notes..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{loading ? 'Updating...' : 'Update GRN'}</span>
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

export default EditGRNModal
